import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import Navbar from '../components/Navbar';
import { format } from 'date-fns';
import { FiPlus, FiCalendar, FiTool, FiLayers, FiTrendingUp, FiCheckCircle, FiChevronDown, FiChevronRight, FiTarget, FiUser, FiUserCheck } from 'react-icons/fi';

const Dashboard = () => {
  const [tasks, setTasks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');
  const { user } = useAuth();

  useEffect(() => {
    fetchTasks();
  }, []);

  const fetchTasks = async () => {
    try {
      const response = await axios.get('/tasks');
      setTasks(response.data);
    } catch (error) {
      console.error('Error fetching tasks:', error);
    } finally {
      setLoading(false);
    }
  };

  const filteredTasks = tasks.filter((task) => {
    if (filter === 'all') return true;
    if (filter === 'my-tasks') {
      return task.createdBy._id === user._id || task.assignedTo?._id === user._id;
    }
    return task.status === filter;
  });

  const topLevelTasks = useMemo(() => filteredTasks.filter((t) => !t.parentTask), [filteredTasks]);
  const subtasksByParent = useMemo(() => {
    const map = {};
    filteredTasks.forEach((t) => {
      const pid = t.parentTask?._id || t.parentTask;
      if (pid) {
        if (!map[pid]) map[pid] = [];
        map[pid].push(t);
      }
    });
    return map;
  }, [filteredTasks]);

  const [expandedMajors, setExpandedMajors] = useState(new Set());
  const toggleMajor = (id) => {
    setExpandedMajors((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500/20 text-green-400 border-green-500/50';
      case 'in-progress':
        return 'bg-blue-500/20 text-blue-400 border-blue-500/50';
      case 'on-hold':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/50';
      default:
        return 'bg-dark-300 text-dark-100 border-dark-300';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'urgent':
        return 'bg-red-500/20 text-red-400';
      case 'high':
        return 'bg-orange-500/20 text-orange-400';
      case 'medium':
        return 'bg-yellow-500/20 text-yellow-400';
      default:
        return 'bg-blue-500/20 text-blue-400';
    }
  };

  const stats = {
    total: tasks.length,
    completed: tasks.filter(t => t.status === 'completed').length,
    inProgress: tasks.filter(t => t.status === 'in-progress').length,
    pending: tasks.filter(t => t.status === 'pending').length,
  };

  if (loading) {
    return (
      <>
        <Navbar />
        <div className="min-h-screen bg-background flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-purple-300"></div>
        </div>
      </>
    );
  }

  return (
    <div className="min-h-screen bg-background relative">
      <Navbar />
      
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 relative z-10">
        {/* Header */}
        <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center mb-8">
          <div>
            <h1 className="text-2xl font-bold text-white mb-2">Dashboard</h1>
            <p className="text-dark-100 text-sm">Manage your tasks</p>
          </div>
          {user?.role !== 'productManager' && (
            <Link
              to="/create-task"
              className="mt-4 sm:mt-0 flex items-center space-x-2 bg-gradient-to-r from-purple-200 to-purple-300 text-white px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
            >
              <FiPlus />
              <span>New Task</span>
            </Link>
          )}
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-8">
          <div className="bg-stroke border border-stroke rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-dark-100 text-sm">Total Tasks</span>
              <FiLayers className="text-purple-300" />
            </div>
            <p className="text-3xl font-bold text-white">{stats.total}</p>
          </div>
          <div className="bg-stroke border border-stroke rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-dark-100 text-sm">Completed</span>
              <FiCheckCircle className="text-green-400" />
            </div>
            <p className="text-3xl font-bold text-white">{stats.completed}</p>
          </div>
          <div className="bg-stroke border border-stroke rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-dark-100 text-sm">In Progress</span>
              <FiTrendingUp className="text-blue-400" />
            </div>
            <p className="text-3xl font-bold text-white">{stats.inProgress}</p>
          </div>
          <div className="bg-stroke border border-stroke rounded-lg p-6">
            <div className="flex items-center justify-between mb-2">
              <span className="text-dark-100 text-sm">Pending</span>
              <FiCalendar className="text-white" />
            </div>
            <p className="text-3xl font-bold text-white">{stats.pending}</p>
          </div>
        </div>

        {/* Filters */}
        <div className="flex flex-wrap gap-2 mb-6">
          {['all', 'my-tasks', 'pending', 'in-progress', 'completed', 'on-hold'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-4 py-2 rounded-lg font-medium transition-colors ${
                filter === f
                  ? 'bg-purple-300 text-white'
                  : 'bg-stroke text-dark-100 hover:bg-dark-300'
              }`}
            >
              {f.split('-').map(word => word.charAt(0).toUpperCase() + word.slice(1)).join(' ')}
            </button>
          ))}
        </div>

        {/* Tasks Grid */}
        {filteredTasks.length === 0 ? (
          <div className="bg-stroke border border-stroke rounded-lg p-12 text-center">
            <FiLayers className="text-6xl text-dark-300 mx-auto mb-4" />
            <h3 className="text-xl font-semibold text-white mb-2">No tasks found</h3>
            <p className="text-dark-100 mb-6">Get started by creating your first task</p>
            {user?.role !== 'productManager' && (
              <Link
                to="/create-task"
                className="inline-flex items-center space-x-2 bg-gradient-to-r from-purple-200 to-purple-300 text-white px-6 py-3 rounded-lg font-semibold hover:opacity-90 transition-opacity"
              >
                <FiPlus />
                <span>New Task</span>
              </Link>
            )}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {topLevelTasks.map((task) => {
              const isMajor = task.isTeamTask;
              const subs = subtasksByParent[task._id] || [];
              const isExpanded = expandedMajors.has(task._id);

              const TaskCard = ({ task: t, isSubtask = false }) => (
                <Link
                  to={`/task/${t._id}`}
                  className={`block rounded-lg p-6 hover:border-purple-300 transition-colors border ${
                    isSubtask ? 'bg-background/80 border-stroke ml-4 pl-5 border-l-2 border-l-purple-300/50' : 'bg-stroke border-stroke'
                  }`}
                >
                  <div className="flex items-start justify-between mb-4">
                    <h3 className={`font-semibold text-white flex-1 ${isSubtask ? 'text-base' : 'text-xl'}`}>
                      {t.title}
                    </h3>
                    <span className={`px-2 py-1 rounded text-xs font-medium border shrink-0 ${getStatusColor(t.status)}`}>
                      {t.status}
                    </span>
                  </div>

                  {t.description && (
                    <p className="text-dark-100 text-sm mb-4 line-clamp-2">{t.description}</p>
                  )}

                  <div className="space-y-3">
                    <div className="flex items-center text-dark-100 text-sm">
                      <FiCalendar className="mr-2 shrink-0 text-white" />
                      <span>Due: {format(new Date(t.deadline), 'MMM dd, yyyy')}</span>
                    </div>
                    <div className="flex flex-wrap gap-x-4 gap-y-1 text-dark-100 text-sm">
                      <span className="flex items-center gap-1">
                        <FiUser className="shrink-0" />
                        {t.createdBy?.name || '—'}
                      </span>
                      <span className="flex items-center gap-1">
                        <FiUserCheck className="shrink-0" />
                        {t.assignedTo?.name || 'Unassigned'}
                      </span>
                    </div>

                    {t.tools && t.tools.length > 0 && (
                      <div className="flex items-start text-dark-100 text-sm">
                        <FiTool className="mr-2 mt-1 shrink-0" />
                        <div className="flex flex-wrap gap-1">
                          {t.tools.slice(0, 3).map((tool, idx) => (
                            <span key={idx} className="bg-dark-300 px-2 py-1 rounded text-xs">
                              {tool}
                            </span>
                          ))}
                          {t.tools.length > 3 && (
                            <span className="text-xs">+{t.tools.length - 3}</span>
                          )}
                        </div>
                      </div>
                    )}

                    {t.technologies && t.technologies.length > 0 && (
                      <div className="flex items-start text-dark-100 text-sm">
                        <FiLayers className="mr-2 mt-1 shrink-0" />
                        <div className="flex flex-wrap gap-1">
                          {t.technologies.slice(0, 2).map((tech, idx) => (
                            <span key={idx} className="bg-purple-300/20 text-purple-200 px-2 py-1 rounded text-xs">
                              {tech}
                            </span>
                          ))}
                          {t.technologies.length > 2 && (
                            <span className="text-xs">+{t.technologies.length - 2}</span>
                          )}
                        </div>
                      </div>
                    )}

                    <div className="flex items-center justify-between pt-3 border-t border-dark-300">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(t.priority)}`}>
                        {t.priority}
                      </span>
                      <div className="text-right">
                        <div className="text-xs text-dark-100">Progress</div>
                        <div className="text-sm font-semibold text-white">{t.progress}%</div>
                      </div>
                    </div>
                  </div>
                </Link>
              );

              if (isMajor) {
                return (
                  <div key={task._id}>
                    <div className="bg-stroke border-2 border-purple-300/60 rounded-lg overflow-hidden">
                      <div className="flex items-start gap-2 p-6">
                        <button
                          type="button"
                          onClick={(e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            toggleMajor(task._id);
                          }}
                          className="flex items-center gap-1 p-1 text-purple-200 hover:text-purple-100 shrink-0 mt-0.5"
                          aria-label={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          {isExpanded ? <FiChevronDown size={20} /> : <FiChevronRight size={20} />}
                          {subs.length > 0 && (
                            <span className="text-xs text-dark-100 hidden sm:inline">{subs.length}</span>
                          )}
                        </button>
                        <Link to={`/task/${task._id}`} className="flex-1 min-w-0">
                          <div className="flex items-center gap-2 mb-3">
                            <FiTarget className="text-purple-300 shrink-0" />
                            <span className="px-2 py-0.5 rounded text-xs font-semibold bg-purple-300/30 text-purple-200 border border-purple-300/50">
                              Major
                            </span>
                          </div>
                          <div className="flex items-start justify-between mb-2">
                            <h3 className="text-xl font-semibold text-white flex-1">{task.title}</h3>
                            <span className={`px-2 py-1 rounded text-xs font-medium border shrink-0 ${getStatusColor(task.status)}`}>
                              {task.status}
                            </span>
                          </div>
                          {task.description && (
                            <p className="text-dark-100 text-sm mb-4 line-clamp-2">{task.description}</p>
                          )}
                          <div className="space-y-3">
                            <div className="flex items-center text-dark-100 text-sm">
                              <FiCalendar className="mr-2 shrink-0 text-white" />
                              <span>Due: {format(new Date(task.deadline), 'MMM dd, yyyy')}</span>
                            </div>
                            <div className="flex flex-wrap gap-x-4 gap-y-1 text-dark-100 text-sm">
                              <span className="flex items-center gap-1">
                                <FiUser className="shrink-0" />
                                {task.createdBy?.name || '—'}
                              </span>
                              <span className="flex items-center gap-1">
                                <FiUserCheck className="shrink-0" />
                                {task.assignedTo?.name || 'Unassigned'}
                              </span>
                            </div>
                            {task.tools && task.tools.length > 0 && (
                              <div className="flex items-start text-dark-100 text-sm">
                                <FiTool className="mr-2 mt-1 shrink-0" />
                                <div className="flex flex-wrap gap-1">
                                  {task.tools.slice(0, 3).map((tool, idx) => (
                                    <span key={idx} className="bg-dark-300 px-2 py-1 rounded text-xs">{tool}</span>
                                  ))}
                                  {task.tools.length > 3 && <span className="text-xs">+{task.tools.length - 3}</span>}
                                </div>
                              </div>
                            )}
                            {task.technologies && task.technologies.length > 0 && (
                              <div className="flex items-start text-dark-100 text-sm">
                                <FiLayers className="mr-2 mt-1 shrink-0" />
                                <div className="flex flex-wrap gap-1">
                                  {task.technologies.slice(0, 2).map((tech, idx) => (
                                    <span key={idx} className="bg-purple-300/20 text-purple-200 px-2 py-1 rounded text-xs">{tech}</span>
                                  ))}
                                  {task.technologies.length > 2 && <span className="text-xs">+{task.technologies.length - 2}</span>}
                                </div>
                              </div>
                            )}
                            <div className="flex items-center justify-between pt-3 border-t border-dark-300">
                              <span className={`px-2 py-1 rounded text-xs font-medium ${getPriorityColor(task.priority)}`}>{task.priority}</span>
                              <div className="text-right">
                                <div className="text-xs text-dark-100">Progress</div>
                                <div className="text-sm font-semibold text-white">{task.progress}%</div>
                              </div>
                            </div>
                          </div>
                        </Link>
                      </div>
                      {isExpanded && subs.length > 0 && (
                        <div className="border-t border-stroke px-6 pb-4 pt-2 space-y-3">
                          <div className="text-dark-100 text-sm font-medium">Subtasks ({subs.length})</div>
                          {subs.map((st) => (
                            <TaskCard key={st._id} task={st} isSubtask />
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                );
              }

              return (
                <div key={task._id}>
                  <TaskCard task={task} isSubtask={false} />
                </div>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
};

export default Dashboard;

